import { Form, Formik, FormikProps } from 'formik'
import { Dispatch, SetStateAction, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import * as Yup from 'yup'
import { useAuth } from '../../auth'
import { getTemplateTokenApi, templateChangeApi } from '../core/_requests'
import { KTSVG, toAbsoluteUrl } from '../../../../_metronic/helpers'
import { InfoCard2 } from '../../widgets/components/UI/InfoCard2'
import TemplateForm from './TemplateForm'
import { TemplateTypeOptions, TemplateTypes } from '../core/_constants'
import DocusealTemplate from './DocusealTemplate'
import { toast } from 'react-toastify'
import { TemplateBasicDetails, TemplateDetails, TemplateFormValues } from '../core/_models'
import Skeleton from 'react-loading-skeleton'
import { ToolTipUIForModal } from '../../widgets/components/UI/ToolTipUIForModal'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import { useThemeMode } from '../../../../_metronic/partials'
import { isMobile } from 'react-device-detect'
import brandingConfig from '../../../../config/brandingConfig'
import { removeNullFields } from '../../../core/_util'

const TemplateModal = ({
  showModal,
  setShowModal,
  setIsTemplateCreateOrEdit,
  templateDetails,
  setTemplateDetails,
  currentTemplateStep,
  setCurrentTemplateStep,
  docusealId,
  setDocusealId,
  isTemplateVisible,
  setIsUpdateTemplateDropdown,
  getTemplatessDashboardCounts,
  viewTemplateLoading,
}: {
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
  setIsTemplateCreateOrEdit: Dispatch<SetStateAction<boolean>>
  templateDetails: TemplateBasicDetails | undefined
  setTemplateDetails: Dispatch<SetStateAction<TemplateDetails | undefined>>
  currentTemplateStep: number
  setCurrentTemplateStep: Dispatch<SetStateAction<number>>
  docusealId: number | undefined
  setDocusealId: Dispatch<SetStateAction<number | undefined>>
  isTemplateVisible: boolean
  setIsUpdateTemplateDropdown: Dispatch<SetStateAction<boolean>>
  getTemplatessDashboardCounts: () => {}
  viewTemplateLoading: boolean
}) => {
  const { formatMessage } = useIntl()
  const { companyId, currentUser } = useAuth()
  const [totalSteps] = useState(2)
  const [loading, setLoading] = useState(false)
  const [templateToken, setTemplateToken] = useState()
  const formikRef = useRef<any>(null)
  const [isInValidTemplate, setIsInValidTemplate] = useState(false)
  const { mode } = useThemeMode()
  const initialValues = {
    templateName: templateDetails?.templateName || '',
    templateType: TemplateTypeOptions.find(
      (type) => type.value === templateDetails?.templateType
    ) || { id: 1, label: TemplateTypes.NDA, value: TemplateTypes.NDA },
  }

  const templateSchema = Yup.object().shape({
    templateName: Yup.string().required(formatMessage({ id: 'Template Name is required' })),
    templateType: Yup.object().shape({
      id: Yup.string().required(formatMessage({ id: 'Template ID is required' })),
      label: Yup.string().required(),
      value: Yup.string().required(),
    }),
  })

  const handleSubmit = async (formValues: TemplateFormValues) => {
    setLoading(true)
    try {
      const {
        data: {
          data: { token },
        },
      } = await getTemplateTokenApi(
        isTemplateVisible,
        formValues.templateName,
        formValues.templateType.value,
        companyId,
        templateDetails?.templateId,
        templateDetails?.templateId ? true : false
      )
      if (token) {
        setTemplateToken(token)
        if (!templateDetails?.templateId) {
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.DigitalSigningTemplateCreatedSuccessful,
              metadata: removeNullFields({
                template_name: formValues.templateName,
                template_type: formValues.templateType,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                company_id: companyId,
              }),
            },
          })
        }
      } else {
        if (!templateDetails?.templateId) {
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.DigitalSigningTemplateCreatedFailed,
              metadata: removeNullFields({
                template_name: formValues.templateName,
                template_type: formValues.templateType,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                company_id: companyId,
              }),
            },
          })
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const prevStep = () => {
    if (currentTemplateStep !== 1) {
      setCurrentTemplateStep((prev) => {
        setIsInValidTemplate(false)
        return prev - 1
      })
    }
  }

  const nextStep = async (isValid: boolean, submitForm: any) => {
    if (isValid) {
      if (currentTemplateStep !== totalSteps) {
        if (templateToken) {
          if (formikRef.current) {
            await handleTemplateChange(
              formikRef.current.values.templateName,
              formikRef.current.values.templateType
            )
          }
        } else {
          await submitForm()
        }
        setCurrentTemplateStep((prev) => {
          return prev + 1
        })
      } else {
        setTemplateDetails(undefined)
        toast.success(
          templateDetails?.templateId
            ? formatMessage({ id: 'Template updated successfully' })
            : formatMessage({ id: 'Template created successfully' })
        )

        setTemplateToken(undefined)
        setIsTemplateCreateOrEdit(true)
        setShowModal(false)
        await getTemplatessDashboardCounts()
        if (!isTemplateVisible) {
          setIsUpdateTemplateDropdown(true)
        }

        if (formikRef.current) {
          formikRef.current.resetForm()
        }
      }
    }
  }

  const handleTemplateChange = async (
    templateName: string,
    templateType: { id: number; label: string; value: string }
  ) => {
    setLoading(true)
    try {
      const {
        data: { data },
      } = await templateChangeApi(
        templateName,
        templateType.value as TemplateTypes,
        companyId!,
        undefined,
        docusealId
      )
      if (data) {
        setDocusealId(undefined)
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DigitalSigningTemplateUpdated,
            metadata: {
              template_name: templateName,
              template_type: templateType.value,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
            },
          },
        })
      }
    } catch (error) {
      console.log(error)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.DigitalSigningTemplateUpdateFailed,
          metadata: {
            template_name: templateName,
            template_type: templateType.value,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
            error: error,
          },
        },
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      show={showModal}
      size='xl'
      onHide={() => {
        setCurrentTemplateStep(1)
        setTemplateToken(undefined)
        setDocusealId(undefined)
        setShowModal(false)
      }}
      aria-labelledby='contained-modal-title-vcenter'
      centered
      className='template-modal-container'
    >
      <Modal.Header>
        <div className='w-100 d-flex justify-content-between align-items-center'>
          <div className='d-flex'>
            <div
              className='d-flex align-items-center cursor-pointer'
              onClick={() => {
                setCurrentTemplateStep(1)
                setTemplateToken(undefined)
                setShowModal(false)
              }}
            >
              <KTSVG
                path={
                  mode === 'dark'
                    ? toAbsoluteUrl('/media/icons/digital-signing/ic_close_dark.svg')
                    : toAbsoluteUrl('/media/icons/digital-signing/ic_close.svg')
                }
              />
            </div>
            <div className={`ps-4 ${isMobile ? 'font-size-13' : 'font-size-20'} fw-bold`}>
              {formatMessage({ id: 'Add New Template for Digital Signing' })}
            </div>
          </div>
          <ToolTipUIForModal tooltipText='Create or upload a new document template for digital sharing' />
        </div>
      </Modal.Header>
      <Formik
        initialValues={initialValues}
        validationSchema={templateSchema}
        onSubmit={templateToken || currentTemplateStep === totalSteps ? () => { } : handleSubmit}
        validateOnMount
        innerRef={formikRef}
        enableReinitialize
      >
        {(formik) => {
          return (
            <Form>
              <Modal.Body>
                <div
                  className='stepper stepper-pills stepper-column d-flex flex-column container'
                  id='kt_create_account_stepper'
                >
                  <div className='card d-flex justify-content-start justify-content-xl-start flex-row-auto overflow-mobile-x w-100  me-9 '>
                    <div className='card-body  py-20 px-0 d-flex justify-content-start'>
                      <div className='stepper-nav'>
                        <div className='stepper-item current custom-marign-right'>
                          <div className='stepper-wrapper'>
                            <div className='stepper-icon w-40px h-40px'>
                              <i className='stepper-check'></i>
                              <span className='stepper-number'>1</span>
                            </div>
                            <div className='stepper-label'>
                              <h3 className='stepper-title fw-semibold font-size-13'>
                                {formatMessage({ id: 'General Details' })}
                              </h3>

                              <div className='stepper-desc font-size-12'>
                                {formatMessage({ id: 'Basic Documents Details' })}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          className={`stepper-item ${currentTemplateStep === 2 ? 'current' : ''
                            } custom-marign-right`}
                        >
                          <div className='stepper-wrapper'>
                            <div className='stepper-icon w-40px h-40px'>
                              <i className='stepper-check '></i>
                              <span className='stepper-number'>2</span>
                            </div>
                            <div className='stepper-label'>
                              <h3 className='stepper-title fw-semibold font-size-13'>
                                {formatMessage({ id: 'Add Document' })}
                              </h3>
                              <div className='stepper-desc font-size-12'>
                                {formatMessage({ id: 'Upload Document' })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {currentTemplateStep === 1 && (
                    <InfoCard2
                      title='How to Add Document Template for Digital Sharing?'
                      desc='Create and upload document templates for easy digital sharing, ensuring consistency and efficiency in your document management.'
                      slug={brandingConfig.helpLinks.digitalSigningDashboardOverview}
                      className='w-100 mb-0'
                      toolTipText='Learn to create or upload a new document template for digital sharing'
                    />
                  )}

                  <div>
                    <div>
                      {currentTemplateStep === 1 && (
                        <div className='current w-100'>
                          {viewTemplateLoading ? (
                            <div>
                              <div className='d-flex justify-content-between gap-2'>
                                <div className='d-flex flex-column'>
                                  <Skeleton
                                    width={150}
                                    height={15}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                    className='mb-2'
                                  />
                                  <Skeleton
                                    width='45vw'
                                    height={40}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                                <div className='d-flex flex-column'>
                                  <Skeleton
                                    width={150}
                                    height={15}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                    className='mb-2'
                                  />
                                  <Skeleton
                                    width='45vw'
                                    height={40}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <TemplateForm formik={formik as FormikProps<TemplateFormValues>} />
                          )}
                        </div>
                      )}
                      {currentTemplateStep === 2 && (
                        <div className='current w-100'>
                          <DocusealTemplate
                            token={templateToken}
                            setDocusealId={setDocusealId}
                            setIsInValidTemplate={setIsInValidTemplate}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer className=' w-full-mobile container'>
                {isMobile ? (
                  <div className='d-md-flex justify-content-md-end flex-wrap flex-nowrap w-full-mobile'>
                    <div className='w-full-mobile'>
                      <button
                        type='submit'
                        className='btn btn-lg btn-primary w-full-mobile'
                        disabled={!formik.isValid || loading || isInValidTemplate}
                        onClick={() => {
                          nextStep(formik.isValid, formik.submitForm)
                        }}
                      >
                        {loading ? (
                          <span className='indicator-label font-size-13 p-0'>
                            {formatMessage({ id: 'Please wait...' })}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <span className='indicator-label'>
                            {currentTemplateStep !== totalSteps
                              ? formatMessage({ id: 'Next' })
                              : templateDetails?.templateId
                                ? formatMessage({ id: 'Save' })
                                : formatMessage({ id: 'Create' })}
                          </span>
                        )}
                      </button>
                    </div>
                    {currentTemplateStep > 1 && (
                      <div className='mr-2 w-full-mobile'>
                        <button
                          onClick={prevStep}
                          type='button'
                          className='btn btn-lg btn-light-secondary text-muted me-3 w-full-mobile mb-3 mb-md-0 custom-button-property'
                        >
                          {formatMessage({ id: 'Back' })}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='d-md-flex justify-content-md-end flex-wrap flex-nowrap w-full-mobile'>
                    {currentTemplateStep > 1 && (
                      <div className='mr-2 w-full-mobile'>
                        <button
                          onClick={prevStep}
                          type='button'
                          className='btn btn-lg btn-light-secondary text-muted me-3 w-full-mobile mb-3 mb-md-0 custom-button-property'
                        >
                          {formatMessage({ id: 'Back' })}
                        </button>
                      </div>
                    )}
                    <div className='w-full-mobile'>
                      <button
                        type='submit'
                        className='btn btn-lg btn-primary w-full-mobile custom-button-property'
                        disabled={!formik.isValid || loading || isInValidTemplate}
                        onClick={() => {
                          nextStep(formik.isValid, formik.submitForm)
                        }}
                      >
                        {loading ? (
                          <span className='indicator-label font-size-13 p-0'>
                            {formatMessage({ id: 'Please wait...' })}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <span className='indicator-label'>
                            {currentTemplateStep !== totalSteps
                              ? formatMessage({ id: 'Next' })
                              : templateDetails?.templateId
                                ? formatMessage({ id: 'Save' })
                                : formatMessage({ id: 'Create' })}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </Modal.Footer>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default TemplateModal
